import React, { useEffect, useState } from 'react';
import { IConfig } from '../../Interfaces/IConfig';
import { CopyrightLextorByte } from '../../CopyrightLextorByte';
import { LoginDriver } from '../Login/LoginDriver';
import { LoginData } from '../Login/LoginData';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { Loader } from '../generic/Loader';
import { ResetPasswordConfirm } from '../public/ResetPasswordConfirm';
import { InviteAcceptContact } from '../public/InviteAcceptContact';
import { getOAuthErrorMessage } from '../../utils/sessionErrors';

interface IProps {
  children: React.ReactElement;
  configObject: IConfig;
}

// Öffentliche Landing-Pages, die unabhängig vom Anmeldestatus erreichbar sein
// müssen (Links aus System-E-Mails). Sie ersetzen die entsprechenden Seiten
// des abgelösten frontend-public-dialog.
const isPublicLandingPath = (pathname: string) =>
  pathname.startsWith('/resetpassword/') ||
  pathname.startsWith('/invite/contact/');

const SecurityDriver: React.FC<IProps> = ({ children, configObject }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isLogged, setIsLogged] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const isPublicLanding = isPublicLandingPath(window.location.pathname);

  useEffect(() => {
    // Öffentliche Landing-Pages benötigen keine Session — Auth-Prüfung
    // überspringen.
    if (isPublicLanding) {
      setIsLoading(false);
      return;
    }

    // Passwort-Reset-Links (/auto_login/...) müssen auch dann funktionieren,
    // wenn der Nutzer bereits eingeloggt ist. Session löschen und als
    // nicht eingeloggt behandeln, damit der Reset-Flow greift.
    if (window.location.pathname.startsWith('/auto_login/')) {
      localStorage.removeItem('SessionToken');
      setIsLogged(false);
      setIsLoading(false);
      return;
    }

    const sessionToken = localStorage.getItem('SessionToken');

    if (!sessionToken) {
      setIsLogged(false);
      setIsLoading(false);
    } else {
      fetch('/api/oauth', {
        method: 'GET',
        headers: {
          Authorization: 'Bearer ' + localStorage.getItem('SessionToken'),
        },
      })
        .then(async res => {
          if (res.status === 200) {
            const data = await res.json();
            localStorage.setItem(
              'TimestampSessionToken',
              data['TimestampSessionToken']
            );
            setIsLogged(true);
            setIsLoading(false);
            return;
          }

          // Fachliche Meldung anhand des Statuscodes bestimmen. Bei 408
          // (Session wegen Inaktivität abgelaufen) zeigt das Portal eine klare
          // Meldung statt eines generischen Fehlers (Ticket Session-Timeout).
          setMsg(getOAuthErrorMessage(res.status));
          setIsLogged(false);
          setIsLoading(false);
        })
        .catch(() => {
          // Netzwerk-/Verbindungsfehler ohne HTTP-Status → generische Meldung.
          setMsg(getOAuthErrorMessage(0));
          setIsLogged(false);
          setIsLoading(false);
        });
    }
  }, []);

  const getTsxBodyLogin = (enableAutoLogin: boolean) => {
    return (
      <>
        <LoginDriver
          enableAutoLogin={enableAutoLogin}
          configObject={configObject}
          msgParent={msg}
        />
        <CopyrightLextorByte configObject={configObject} />
      </>
    );
  };

  if (isPublicLanding) {
    return (
      <>
        <Router>
          <Routes>
            <Route
              path='/resetpassword/:idContact/:token'
              element={<ResetPasswordConfirm configObject={configObject} />}
            />
            <Route
              path='/invite/contact/:token'
              element={<InviteAcceptContact configObject={configObject} />}
            />
          </Routes>
        </Router>
        <CopyrightLextorByte configObject={configObject} />
      </>
    );
  }

  if (isLoading) {
    return <Loader />;
  } else if (
    isLogged &&
    localStorage.getItem('passwordResetRequired') !== 'true'
  ) {
    return children;
  } else if (
    isLogged &&
    localStorage.getItem('passwordResetRequired') === 'true'
  ) {
    return <LoginData configObject={configObject} />;
  } else {
    return (
      <>
        <Router>
          <Routes>
            <Route
              path='/auto_login/:email/:emailProvider/:pw'
              element={getTsxBodyLogin(true)}
            />
            <Route index element={getTsxBodyLogin(false)} />
            <Route path='/*' element={getTsxBodyLogin(false)} />
          </Routes>
        </Router>
      </>
    );
  }
};
export default SecurityDriver;
